// ============================================================================
// ProfileCard — the read-only PROFILE summary: a definition list of the client's
// details plus the readable weekly-availability view. An Edit button opens the
// ProfileEditForm drawer (owned by the page). Optional-first: only fields that
// have a value are shown, with a single "Add details" nudge when the profile is
// essentially empty.
// ============================================================================
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { Button, Card, CardHeader } from "../../components";
import type { Client } from "../../api";
import { useAccount } from "../../hooks/account";
import { currencySymbol, formatMoney, isRateType } from "../../lib/currency";
import { AvailabilityView } from "./Availability";
import { ageFromBirthday, fmtDate, normalizeSex, parseAvailability } from "./lib";

interface ProfileCardProps {
  client: Client;
  onEdit: () => void;
}

export function ProfileCard({ client, onEdit }: ProfileCardProps) {
  const { t, i18n } = useTranslation("clientDetail");
  const account = useAccount();
  const currency = account.data?.default_currency ?? null;
  const symbol = currencySymbol(currency);
  const sex = normalizeSex(client.sex);
  const age = ageFromBirthday(client.birthday);
  const parsed = parseAvailability(client.availability);

  const rows: Array<{ label: string; value: ReactNode }> = [];
  if (sex) rows.push({ label: t("profile.sex"), value: t(`sex.${sex}`) });
  if (client.birthday) {
    rows.push({
      label: t("profile.birthday"),
      value: (
        <>
          {fmtDate(client.birthday)}
          {age != null && (
            <span className="text-body-soft"> · {t("header.age", { count: age })}</span>
          )}
        </>
      ),
    });
  }
  if (client.height_cm != null) {
    rows.push({
      label: t("profile.height"),
      value: t("header.height", { value: client.height_cm }),
    });
  }
  if (client.occupation) {
    rows.push({ label: t("profile.occupation"), value: client.occupation });
  }
  // Charge model — only when a rate is actually on file.
  if (isRateType(client.rate_type) && client.rate_amount != null) {
    const money = formatMoney(client.rate_amount, currency, {
      symbol,
      locale: i18n.language,
    });
    const unit =
      client.rate_type === "per_hour"
        ? t("charge.unitPerHour")
        : t("charge.unitPerSession");
    rows.push({
      label: t("charge.rate"),
      value: (
        <>
          {money}
          <span className="text-body-soft"> {unit}</span>
        </>
      ),
    });
  }

  const hasAnything =
    rows.length > 0 || client.remarks || client.availability;

  return (
    <Card>
      <CardHeader
        title={t("profile.title")}
        action={
          <Button size="sm" variant="ghost" onClick={onEdit}>
            {t("actions.edit")}
          </Button>
        }
      />

      {!hasAnything ? (
        <div className="rounded-md bg-surface-sunken px-4 py-6 text-center">
          <p className="text-sm text-body-soft">{t("profile.emptyBlurb")}</p>
          <Button size="sm" className="mt-3" onClick={onEdit}>
            {t("profile.addDetails")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.length > 0 && (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              {rows.map((row) => (
                <div key={row.label} className="flex flex-col">
                  <dt className="text-xs font-bold uppercase tracking-kicker text-body-soft">
                    {row.label}
                  </dt>
                  <dd className="mt-0.5 text-base font-medium text-body">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {client.remarks && (
            <div>
              <dt className="text-xs font-bold uppercase tracking-kicker text-body-soft">
                {t("profile.remarks")}
              </dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-body-muted">
                {client.remarks}
              </dd>
            </div>
          )}

          <div>
            <dt className="mb-1.5 text-xs font-bold uppercase tracking-kicker text-body-soft">
              {t("profile.availability")}
            </dt>
            <dd>
              <AvailabilityView weekly={parsed.weekly} legacyText={parsed.legacyText} />
            </dd>
          </div>
        </div>
      )}
    </Card>
  );
}
